import { styled } from '@linaria/react';
import { Group, RingProgress, Stack } from '@mantine/core';
import { Tooltip } from '../../../../ui-library/tooltip/tooltip';
import { Text } from '../../../../ui-library/typography/typography';
import type { TokenUsage } from '../../flora.type';
import { formatTokenCount, getProgressColor } from './context-usage-indicator.helpers';

interface ContextUsageIndicatorProps {
  tokenUsage: TokenUsage | null;
  dataTheme?: string;
}

export const ContextUsageIndicator = ({ tokenUsage, dataTheme }: ContextUsageIndicatorProps) => {
  if (!tokenUsage) {
    return null;
  }

  const { context_used, context_limit, percentage } = tokenUsage;
  const color = getProgressColor(percentage);
  const isDarkMode = dataTheme === 'dark';

  const tooltipContent = (
    <Stack gap={4}>
      <Text weight="bold" style={{ textAlign: 'right', width: '100%' }}>
        {percentage}% Context Used
      </Text>
      <Group justify="space-between">
        <Text weight="bold">Used:</Text>
        <Text>{formatTokenCount(context_used)} tokens</Text>
      </Group>
      <Group justify="space-between">
        <Text weight="bold">Limit:</Text>
        <Text>{formatTokenCount(context_limit)} tokens</Text>
      </Group>
      <Group justify="space-between">
        <Text weight="bold">Remaining:</Text>
        <Text>{formatTokenCount(context_limit - context_used)} tokens</Text>
      </Group>
    </Stack>
  );

  return (
    <Tooltip label={tooltipContent} position="top" withArrow multiline w={250} variant="black" withinPortal={false}>
      <IndicatorContainer>
        <RingProgress
          size={32}
          thickness={3}
          roundCaps
          sections={[{ value: percentage, color }]}
          rootColor={isDarkMode ? 'var(--ink-darker)' : 'var(--sky-light)'}
        />
      </IndicatorContainer>
    </Tooltip>
  );
};

const IndicatorContainer = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
`;
